// Fuente ÚNICA (10.1) de parseo de la liquidación de sueldo LEGAL (PDF de la contadora Anayli,
// formato fijo de Merinell SpA). Función PURA: recibe el TEXTO ya extraído del PDF (la extracción
// de texto — pdfjs-dist en Fase C, `pdftotext -layout` en el script de dry-run de Fase B — es un
// problema aparte, de I/O, deliberadamente separado de esta función) y devuelve el registro
// parseado + validado, o el rechazo explícito con el motivo (10.177 — nunca falla en silencio).
//
// Identificación de empleado y período: SIEMPRE por CONTENIDO del PDF (RUT + "Liquidación de Sueldo
// de <Mes> de <Año>"), NUNCA por nombre de archivo ni por nombre de persona (10.7) — el caller no le
// pasa a esta función ni el nombre del archivo ni el nombre de la persona; recibe el `empleadosPorRut`
// (Map RUT normalizado → fila de `empleados`) y resuelve el vínculo real por RUT.
//
// ── HALLAZGO REAL (revisión de Cowork, 2026-09-28) — corregido en esta versión ──────────────────
// El PDF tiene una sección OPCIONAL "Descuentos Varios" (ej. "Anticipo .: 100.000") ANTES de
// "Descuentos Legales", con su propio "Total Descuentos Varios". La versión anterior de este parser
// la ignoraba COMPLETO: no capturaba sus líneas, y el `total_descuentos` (el "Total Descuentos"
// general que SÍ incluye Descuentos Varios) se calculaba para la validación interna pero NUNCA se
// guardaba en `data` — el registro que se hubiera persistido habría quedado matemáticamente
// contradictorio (`total_haberes − total_descuentos_legales ≠ liquido` para cualquier mes con
// Descuentos Varios). La validación de fondo (`Total Haberes − Total Descuentos = Líquido`) SÍ
// usaba el total correcto internamente (por eso no rechazaba los 3 casos reales con anticipo — la
// cuenta CERRABA), pero el dato de "por qué" cerraba (el desglose de Descuentos Varios) se perdía
// en silencio. Ver docs/decisiones/2026-09-28-liquidaciones-contadora-fase-b-fix-descuentos-varios.md
// para el diagnóstico completo con los 3 casos reales.
//
// Fix: se captura "Descuentos Varios" con el MISMO patrón que "Haberes" (jsonb de líneas + total
// propio), se guarda el "Total Descuentos" general (antes descartado), y CUALQUIER línea dentro de
// un bloque itemizado (Haberes / Descuentos Varios) que no matchee "etiqueta: monto" ahora RECHAZA
// el documento explícito — nunca se ignora texto sin explicar por qué (10.177).

const MESES = {
  enero: 1,
  febrero: 2,
  marzo: 3,
  abril: 4,
  mayo: 5,
  junio: 6,
  julio: 7,
  agosto: 8,
  septiembre: 9,
  setiembre: 9,
  octubre: 10,
  noviembre: 11,
  diciembre: 12,
};

// Formato chileno: "." separador de miles, "," separador decimal ("40.873,77" → 40873.77).
// Los montos en pesos de este documento no traen decimales ("1.334.999"), pero UF sí.
const parseMontoCL = (crudo) => {
  if (crudo == null) return null;
  const limpio = String(crudo).trim().replace(/\$/g, "");
  if (limpio === "") return null;
  const normalizado = limpio.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(normalizado);
  return Number.isNaN(n) ? null : n;
};

// Un monto entre paréntesis en este documento es un descuento mostrado como "(247.704)" — la
// magnitud siempre se guarda POSITIVA (el signo es una convención de despliegue del PDF, no un dato
// de negocio distinto); "0" sin paréntesis (Impuesto Único en tramo exento) también es válido.
const parseMontoDescuento = (crudo) => {
  if (crudo == null) return null;
  const limpio = String(crudo)
    .trim()
    .replace(/^\(|\)$/g, "");
  return parseMontoCL(limpio);
};

const normalizarRut = (rut) => (rut || "").replace(/\s/g, "").toUpperCase();

const TOLERANCIA_PESOS = 1; // redondeo de la contadora, nunca más de $1 (verificado con los 27 reales)

// Línea de un bloque ITEMIZADO (Haberes / Descuentos Varios): "Etiqueta:   monto". Reusado por los
// dos bloques — 10.1, un solo patrón de "línea con etiqueta y monto" en todo el parser.
const RE_LINEA_ITEM = /^(.+?):[\t ]+([\d,.]+)$/;

/**
 * Parsea un bloque itemizado (líneas "Etiqueta: monto") entre dos marcadores de texto. Devuelve
 * `{ items, lineaNoReconocida }` — si CUALQUIER línea no vacía del bloque no matchea el patrón
 * "etiqueta: monto", `lineaNoReconocida` trae el texto crudo de esa línea (nunca se ignora — 10.177).
 * `items` puede ser `[]` si el bloque no existe en el documento (bloque opcional, ej. Descuentos
 * Varios en un mes sin anticipos) — eso lo decide el caller mirando si encontró los marcadores.
 */
const parsearBloqueItemizado = (bloqueTexto) => {
  const items = [];
  const lineas = bloqueTexto
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l !== "");
  for (const linea of lineas) {
    const m = linea.match(RE_LINEA_ITEM);
    if (!m) return { items, lineaNoReconocida: linea };
    items.push({ etiqueta: m[1].trim(), monto: parseMontoCL(m[2]) });
  }
  return { items, lineaNoReconocida: null };
};

/**
 * @param {string} texto  Texto YA extraído del PDF (ver cabecera — extracción es responsabilidad del caller).
 * @param {{ empleadosPorRut: Map<string, {id:string, nombre:string, apellido:string, rut:string}> }} deps
 * @returns {{ ok:true, data:object } | { ok:false, motivo:string, rutExtraido?:string, periodo?:string }}
 */
export const parsearLiquidacionTexto = (texto, { empleadosPorRut } = {}) => {
  if (!texto || typeof texto !== "string" || texto.trim() === "") {
    throw new Error("parsearLiquidacionTexto: texto vacío — falla de extracción del PDF, no un rechazo de dato");
  }

  // ── Período ──────────────────────────────────────────────────────────────────────────────────
  const matchPeriodo = texto.match(/Liquidaci[oó]n de Sueldo de\s+(\p{L}+)\s+de\s+(\d{4})/iu);
  if (!matchPeriodo) {
    throw new Error(
      "parsearLiquidacionTexto: no se encontró 'Liquidación de Sueldo de <Mes> de <Año>' — formato inesperado, no un PDF de este tipo"
    );
  }
  const mesNombre = matchPeriodo[1].toLowerCase();
  const periodo_mes = MESES[mesNombre];
  const periodo_año = parseInt(matchPeriodo[2], 10);
  if (!periodo_mes) {
    throw new Error(`parsearLiquidacionTexto: mes "${matchPeriodo[1]}" no reconocido`);
  }

  // ── RUT del empleado — SIEMPRE el primer "RUT:" que aparece DESPUÉS del encabezado de período
  // (el primer "RUT:" del documento, antes del período, es el de la empresa Merinell SpA — 10.7,
  // nunca se identifica por nombre). ──────────────────────────────────────────────────────────
  const textoPostPeriodo = texto.slice(matchPeriodo.index + matchPeriodo[0].length);
  const matchRutEmpleado = textoPostPeriodo.match(/RUT:\s*([\d.]+-[\dKk])/);
  if (!matchRutEmpleado) {
    throw new Error("parsearLiquidacionTexto: no se encontró el RUT del empleado tras el encabezado de período");
  }
  const rutExtraido = normalizarRut(matchRutEmpleado[1]);
  const periodoLabel = `${String(periodo_mes).padStart(2, "0")}-${periodo_año}`;

  // ── Validación: RUT existe en la ficha de empleados (Chile) — si no, FRENA y reporta (10.28,
  // "si falta el RUT en la ficha, frenar y reportar"). No se crea vínculo a ciegas (10.4/10.7). ──
  const empleado = empleadosPorRut?.get(rutExtraido);
  if (!empleado) {
    return {
      ok: false,
      motivo: `RUT ${matchRutEmpleado[1]} (del PDF) no matchea ningún empleado Chile con RUT cargado en la ficha — frenar y reportar, no se inventa el vínculo`,
      rutExtraido: matchRutEmpleado[1],
      periodo: periodoLabel,
    };
  }

  // ── Bloque de Haberes: entre "Haberes Tributables e Imponibles" (header de sección) y "Total
  // Haberes Tributables e Imponibles" — CUALQUIER línea no reconocida rechaza (10.177). ──────────
  const idxHaberesHeaderInicio = texto.indexOf("Haberes Tributables e Imponibles");
  const idxHaberesFin = texto.indexOf("Total Haberes Tributables e Imponibles");
  if (idxHaberesHeaderInicio === -1 || idxHaberesFin === -1) {
    throw new Error("parsearLiquidacionTexto: no se encontró el bloque de Haberes Tributables e Imponibles");
  }
  const idxHaberesHeaderFin = idxHaberesHeaderInicio + "Haberes Tributables e Imponibles".length;
  const { items: haberes, lineaNoReconocida: lineaHaberesNoReconocida } = parsearBloqueItemizado(
    texto.slice(idxHaberesHeaderFin, idxHaberesFin)
  );
  if (lineaHaberesNoReconocida) {
    return {
      ok: false,
      motivo: `Línea no reconocida en la sección "Haberes Tributables e Imponibles": "${lineaHaberesNoReconocida}" — nunca se ignora texto (10.177)`,
      rutExtraido: matchRutEmpleado[1],
      periodo: periodoLabel,
    };
  }

  // ── Bloque de Descuentos Varios — OPCIONAL (solo existe si hubo anticipo u otro descuento no
  // legal ese mes). Mismo patrón que Haberes: entre el header y su "Total Descuentos Varios". ────
  const idxDVHeaderInicio = texto.indexOf("Descuentos Varios");
  const idxDVFin = texto.indexOf("Total Descuentos Varios");
  const hayDescuentosVarios = idxDVHeaderInicio !== -1 && idxDVFin !== -1;
  let descuentos_varios = [];
  let total_descuentos_varios = 0;
  if (hayDescuentosVarios) {
    const idxDVHeaderFin = idxDVHeaderInicio + "Descuentos Varios".length;
    const { items, lineaNoReconocida: lineaDVNoReconocida } = parsearBloqueItemizado(
      texto.slice(idxDVHeaderFin, idxDVFin)
    );
    if (lineaDVNoReconocida) {
      return {
        ok: false,
        motivo: `Línea no reconocida en la sección "Descuentos Varios": "${lineaDVNoReconocida}" — nunca se ignora texto (10.177)`,
        rutExtraido: matchRutEmpleado[1],
        periodo: periodoLabel,
      };
    }
    descuentos_varios = items;
    const matchTotalDV = texto.match(/Total Descuentos Varios[\t ]+\(?([\d,.]+)\)?/);
    total_descuentos_varios = matchTotalDV ? parseMontoDescuento(matchTotalDV[1]) : null;
  }

  // ── Totales del resumen final — "Total Haberes" / "Total Descuentos" / "Total Líquido" (los
  // labels LIMPIOS, sin "Tributables e Imponibles"/"Legales"/"Varios", que la contadora repite al
  // pie). "Total Descuentos" acá es el GENERAL (legales + varios) — antes se calculaba y se
  // descartaba; ahora se guarda en `data` (el hallazgo de Cowork). ──────────────────────────────
  const matchTotalHaberes = texto.match(/\n[\t ]*Total Haberes[\t ]+([\d,.]+)/);
  const matchTotalDescuentos = texto.match(/\n[\t ]*Total Descuentos[\t ]+\(?([\d,.]+)\)?/);
  const matchTotalLiquido = texto.match(/\n[\t ]*Total L[ií]quido[\t ]+([\d,.]+)/);
  if (!matchTotalHaberes || !matchTotalDescuentos || !matchTotalLiquido) {
    throw new Error(
      "parsearLiquidacionTexto: no se encontraron los 3 totales del resumen (Total Haberes/Descuentos/Líquido)"
    );
  }
  const total_haberes = parseMontoCL(matchTotalHaberes[1]);
  const total_descuentos = parseMontoDescuento(matchTotalDescuentos[1]);
  const liquido = parseMontoCL(matchTotalLiquido[1]);

  // ── Descuentos legales — cada pieza del desglose (estructura fija, no itemizada libre; el
  // cross-check de abajo, AFP+Salud+Cesantía+Impuesto == Total Descuentos Legales, es la red de
  // seguridad de ESTE bloque — 10.177 equivalente al "línea no reconocida" de los bloques libres). ──
  const matchImponiblePrevisional = texto.match(/Imponible Previsional:\s*\$?([\d,.]+)/);
  const matchTotalAfp = texto.match(/Total AFP[\t ]+\(?([\d,.]+)\)?/);
  const esIsapre = /\bisapre\b/i.test(texto);
  const matchTotalSalud = texto.match(/Total Salud[\t ]+\(?([\d,.]+)\)?/);
  const matchCesantia = texto.match(/Seguro de Cesant[ií]a[^(]*\(Imponible:[^)]*\)[\t ]+\(?([\d,.]+)\)?/);
  const matchBaseImpuesto = texto.match(/impuesto unico:\s*\(base:\s*\$?([\d,.]+)\)[\t ]+\(?([\d,.]+)\)?/i);
  const matchTotalDescuentosLegales = texto.match(/Total Descuentos Legales[\t ]+\(?([\d,.]+)\)?/);
  if (!matchTotalAfp || !matchTotalSalud || !matchCesantia || !matchBaseImpuesto || !matchTotalDescuentosLegales) {
    throw new Error(
      "parsearLiquidacionTexto: no se encontró alguna de las piezas fijas de Descuentos Legales (AFP/Salud/Cesantía/Impuesto)"
    );
  }
  const afp_monto = parseMontoDescuento(matchTotalAfp[1]);
  const salud_monto = parseMontoDescuento(matchTotalSalud[1]);
  const cesantia_monto = parseMontoDescuento(matchCesantia[1]);
  const impuesto_unico_monto = parseMontoDescuento(matchBaseImpuesto[2]);
  const total_descuentos_legales = parseMontoDescuento(matchTotalDescuentosLegales[1]);

  const data = {
    empleado_id: empleado.id,
    pais: "Chile",
    periodo_año,
    periodo_mes,
    haberes,
    total_haberes,
    descuentos_varios,
    total_descuentos_varios,
    imponible_previsional: matchImponiblePrevisional ? parseMontoCL(matchImponiblePrevisional[1]) : null,
    afp_monto,
    salud_tipo: esIsapre ? "isapre" : "fonasa",
    salud_monto,
    cesantia_monto,
    base_impuesto: parseMontoCL(matchBaseImpuesto[1]),
    impuesto_unico_monto,
    total_descuentos_legales,
    total_descuentos,
    liquido,
    texto_crudo: texto,
  };

  // ── UF/UTM del mes (tal cual el PDF de la contadora — para comparar contra la UF/UTM que usa el
  // motor, P3.46). ─────────────────────────────────────────────────────────────────────────────
  const matchUfUtm = texto.match(/UF del mes:\s*\$?([\d,.]+),?\s*UTM del mes:\s*\$?([\d,.]+)/);
  data.uf_mes = matchUfUtm ? parseMontoCL(matchUfUtm[1]) : null;
  data.utm_mes = matchUfUtm ? parseMontoCL(matchUfUtm[2]) : null;

  // ═══════════════════════════════════════════════════════════════════════════════════════════
  // VALIDACIONES (10.177 — rechazan con motivo explícito, nunca a ciegas)
  // ═══════════════════════════════════════════════════════════════════════════════════════════

  // 1. Σ haberes individuales == Total Haberes declarado.
  const sumaHaberes = haberes.reduce((s, h) => s + (h.monto || 0), 0);
  if (Math.abs(sumaHaberes - total_haberes) > TOLERANCIA_PESOS) {
    return {
      ok: false,
      motivo: `Σ haberes individuales (${sumaHaberes}) ≠ Total Haberes declarado (${total_haberes}) — diferencia $${Math.round(sumaHaberes - total_haberes)}`,
      rutExtraido: matchRutEmpleado[1],
      periodo: periodoLabel,
      data,
    };
  }

  // 2. Σ descuentos varios == Total Descuentos Varios declarado (si la sección existe).
  if (hayDescuentosVarios) {
    const sumaDV = descuentos_varios.reduce((s, d) => s + (d.monto || 0), 0);
    if (Math.abs(sumaDV - total_descuentos_varios) > TOLERANCIA_PESOS) {
      return {
        ok: false,
        motivo: `Σ Descuentos Varios (${sumaDV}) ≠ Total Descuentos Varios declarado (${total_descuentos_varios}) — diferencia $${Math.round(sumaDV - total_descuentos_varios)}`,
        rutExtraido: matchRutEmpleado[1],
        periodo: periodoLabel,
        data,
      };
    }
  }

  // 3. Total Descuentos Legales + Total Descuentos Varios == Total Descuentos (general).
  const descuentosLegalesMasVarios = total_descuentos_legales + total_descuentos_varios;
  if (Math.abs(descuentosLegalesMasVarios - total_descuentos) > TOLERANCIA_PESOS) {
    return {
      ok: false,
      motivo: `Total Descuentos Legales (${total_descuentos_legales}) + Total Descuentos Varios (${total_descuentos_varios}) = ${descuentosLegalesMasVarios} ≠ Total Descuentos declarado (${total_descuentos}) — diferencia $${Math.round(descuentosLegalesMasVarios - total_descuentos)}`,
      rutExtraido: matchRutEmpleado[1],
      periodo: periodoLabel,
      data,
    };
  }

  // 4. Cross-check del bloque Descuentos Legales (red de seguridad equivalente a "línea no
  // reconocida" para un bloque de estructura fija, no itemizada libre): AFP + Salud + Cesantía +
  // Impuesto Único == Total Descuentos Legales declarado. Si hubiera una línea legal extra que el
  // parser no captura por nombre, acá se nota (nunca se ignora — 10.177).
  const sumaPiezasLegales = afp_monto + salud_monto + cesantia_monto + impuesto_unico_monto;
  if (Math.abs(sumaPiezasLegales - total_descuentos_legales) > TOLERANCIA_PESOS) {
    return {
      ok: false,
      motivo: `AFP (${afp_monto}) + Salud (${salud_monto}) + Cesantía (${cesantia_monto}) + Impuesto Único (${impuesto_unico_monto}) = ${sumaPiezasLegales} ≠ Total Descuentos Legales declarado (${total_descuentos_legales}) — puede haber una línea de descuento legal no reconocida`,
      rutExtraido: matchRutEmpleado[1],
      periodo: periodoLabel,
      data,
    };
  }

  // 5. Total Haberes − Total Descuentos (general) == Total Líquido declarado.
  const liquidoCalculado = total_haberes - total_descuentos;
  if (Math.abs(liquidoCalculado - liquido) > TOLERANCIA_PESOS) {
    return {
      ok: false,
      motivo: `Total Haberes (${total_haberes}) − Total Descuentos (${total_descuentos}) = ${liquidoCalculado} ≠ Total Líquido declarado (${liquido}) — diferencia $${Math.round(liquidoCalculado - liquido)}`,
      rutExtraido: matchRutEmpleado[1],
      periodo: periodoLabel,
      data,
    };
  }

  return { ok: true, data, empleado, periodo: periodoLabel };
};
